import React from 'react'
import { Alert, Form } from 'react-bootstrap'
import { BsPrefixProps } from 'react-bootstrap/esm/helpers'
import { cn } from '../utils/addToClassName'
import { Require } from '../utils/require'
import {
  FormBooleanFieldsetItemProps,
  FormErrorMessageProps,
  FormLabelProps,
  FormUtils,
  PasswordScoreTypes,
  PasswordToggleTypes,
} from './formUtils'
import { SVGIcon } from '../utils/svg'

// ==========================================================================
// FORM CONTROLS & BUTTONS
// ==========================================================================

export type FormItemBaseProps = {
  controlId: string
  required: boolean
  disabled?: boolean
  readOnly?: boolean
  fieldMessage?: string
  extraGroupClasses?: string
  onBlur?: React.FocusEventHandler<HTMLElement>
} & FormLabelProps

// Base props with required label
export type FormItemBasePropsWithLabel = Require<FormItemBaseProps, 'label'>

export type FormControlProps = {
  as?: BsPrefixProps['as']
  type?: string
  rows?: number
  value: string
  onChange: (_: string) => void
  onFocus?: (_: string) => void
  placeholder?: string
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
  showPasswordToggle?: PasswordToggleTypes
  passwordScore?: PasswordScoreTypes
  min?: string
  max?: string
  maxLength?: number
} & FormItemBasePropsWithLabel &
  FormErrorMessageProps

type FormSelectProps = {
  options: { label: string; value: string; disabled?: boolean }[]
  value: string
  onChange: (_: string) => void
} & FormItemBasePropsWithLabel &
  FormErrorMessageProps

type FormDataSetProps = {
  options: { value: string; disabled?: boolean }[]
  value: string
  placeholder?: string
  onChange: (_: string) => void
} & FormItemBasePropsWithLabel &
  FormErrorMessageProps

type FormBooleanFieldsetProps = {
  items: FormBooleanFieldsetItemProps[]
} & FormItemBaseProps &
  FormErrorMessageProps

type FormSwitchProps = {
  checked: boolean
  onChange: (_: boolean) => void
} & FormItemBasePropsWithLabel

type FormUploadProps = {
  accept?: string
  onChange: (_: React.ChangeEvent<HTMLInputElement>) => void
} & FormItemBaseProps &
  FormErrorMessageProps

const fieldErrorClassName = 'fs__invalid is-invalid'

export const FormControls = {
  FormItem: (props: FormControlProps) => {
    const {
      as,
      controlId,
      disabled,
      errorMessageId,
      fieldMessage,
      inputMode,
      invalid,
      max,
      maxLength,
      min,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      readOnly,
      required,
      rows,
      type,
      value,
    } = props
    const errorId = errorMessageId || `a11y-${controlId}-form-error`
    const fieldMessageId = `a11y-${controlId}-form-message`
    return (
      <Form.Control
        type={type}
        as={as}
        rows={rows}
        value={value}
        placeholder={placeholder}
        inputMode={
          inputMode ||
          (type?.includes('mail')
            ? 'email'
            : type?.includes('tel')
            ? 'tel'
            : type?.includes('search')
            ? 'search'
            : 'text')
        }
        readOnly={readOnly}
        disabled={disabled}
        aria-required={required}
        isInvalid={invalid}
        aria-invalid={invalid}
        aria-describedby={invalid ? errorId : fieldMessage ? fieldMessageId : undefined}
        onChange={(e) => onChange(e.currentTarget.value)}
        onBlur={(e) => {
          if (onBlur) onBlur(e)
          if (e.currentTarget.value == '') {
            e.currentTarget.closest('.fs__group')?.classList.remove('fs--focused')
          }
        }}
        onFocus={(e) => {
          if (onFocus) onFocus(e.currentTarget.value)
          if (e.currentTarget.value == '') {
            e.currentTarget.closest('.fs__group')?.classList.add('fs--focused')
          }
        }}
        className="fs__input"
        maxLength={maxLength}
        min={min}
        max={max}
      />
    )
  },
  FormItemInGroup: (props: FormControlProps) => {
    const { controlId, extraGroupClasses, passwordScore, showPasswordToggle, invalid, errorMessageId } = props
    const errorId = errorMessageId || `a11y-${controlId}-form-error`
    const fieldMessageId = `a11y-${controlId}-form-message`
    return (
      <FormUtils.Group {...props} extraGroupClasses={`fs__group--input ${extraGroupClasses || ''}`}>
        <FormUtils.Label {...props} />

        {(controlId.toLowerCase().includes('password') || controlId.toLowerCase().includes('pw')) &&
        showPasswordToggle ? (
          // When input has a `showPasswordToggle`, wrap it in a div to position the PasswordToggle properly
          // Also, add error classes to this div, because the form validation texts are show only when .is-invalid is a sibling of .invalid-feedback
          <div className={`fs__input-wrapper${invalid ? ' is-invalid' : ''}`}>
            <FormControls.FormItem {...props} />
            <FormUtils.PasswordToggle {...props} />
          </div>
        ) : (
          <FormControls.FormItem {...props} />
        )}

        {props.inputMode == 'search' && <SVGIcon className='fs__icon--search' id='icon-search' width={24} height={24} />}

        {/* Password strength bar */}
        {passwordScore != undefined && (
          <FormUtils.PasswordStrengthBar
            password={props.value}
            passwordScore={passwordScore}
            errorMessageId={errorId}
          />
        )}

        {/* Validation feedback & Field message */}
        <FormUtils.ValidationTexts {...props} errorMessageId={errorId} fieldMessageId={fieldMessageId} />
      </FormUtils.Group>
    )
  },
  FormTextareaInGroup: (props: FormControlProps) => {
    const { as, ...rest } = props
    return <FormControls.FormItemInGroup {...rest} as="textarea" rows={rest.rows || 6} />
  },
  FormSelectInGroup: (props: FormSelectProps) => {
    const {
      extraGroupClasses,
      options,
      controlId,
      errorMessageId,
      fieldMessage,
      invalid,
      onBlur,
      onChange,
      required,
      disabled,
      value,
    } = props
    const errorId = errorMessageId || `a11y-${controlId}-form-error`
    const fieldMessageId = `a11y-${controlId}-form-message`
    return (
      <FormUtils.Group {...props} extraGroupClasses={`fs__group--select ${extraGroupClasses || ''}`}>
        <FormUtils.Label {...props} />

        <Form.Select
          value={value}
          disabled={disabled}
          aria-required={required}
          isInvalid={invalid ? true : undefined}
          aria-invalid={invalid}
          aria-describedby={invalid ? errorId : fieldMessage != undefined ? fieldMessageId : undefined}
          className="fs__select"
          onChange={(e) => onChange(e.currentTarget.value)}
          onBlur={onBlur}
        >
          {options.map((option) => (
            <option key={option.label} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </Form.Select>

        {/* Validation feedback & Field message */}
        <FormUtils.ValidationTexts {...props} errorMessageId={errorId} fieldMessageId={fieldMessageId} />
      </FormUtils.Group>
    )
  },
  FormDataListInGroup: (props: FormDataSetProps) => {
    const {
      controlId,
      disabled,
      errorMessageId,
      fieldMessage,
      invalid,
      onBlur,
      onChange,
      options,
      placeholder,
      readOnly,
      required,
      value,
    } = props
    const errorId = errorMessageId || `a11y-${controlId}-form-error`
    const fieldMessageId = `a11y-${controlId}-form-message`
    const inputId = `dl-input-${controlId}`
    const datalistId = `dl-datalist-${controlId}`
    return (
      <FormUtils.Group {...props}>
        <FormUtils.Label {...props} />
        <input
          id={inputId}
          list={datalistId}
          value={value || ''}
          disabled={disabled}
          readOnly={readOnly}
          placeholder={placeholder}
          aria-required={required}
          aria-describedby={invalid ? errorId : fieldMessage ? fieldMessageId : undefined}
          className={'form-select fs__datalist' + (invalid ? ` ${fieldErrorClassName}` : '')}
          onChange={(e) => onChange(e.currentTarget.value)}
          onBlur={onBlur}
        />

        <datalist id={datalistId}>
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.value}
            </option>
          ))}
        </datalist>

        {/* Validation feedback & Field message */}
        <FormUtils.ValidationTexts {...props} errorMessageId={errorId} fieldMessageId={fieldMessageId} />
      </FormUtils.Group>
    )
  },
  FormUploadInGroup: (props: FormUploadProps) => {
    const {
      accept,
      controlId,
      extraGroupClasses,
      invalid,
      disabled,
      readOnly,
      required,
      errorMessageId,
      fieldMessage,
      onChange,
      onBlur,
    } = props
    const errorId = errorMessageId || `a11y-${controlId}-form-error`
    const fieldMessageId = `a11y-${controlId}-form-message`
    return (
      <FormUtils.Group {...props} extraGroupClasses={`fs__group--upload ${extraGroupClasses || ''}`}>
        <FormUtils.Label {...props} />

        <Form.Control
          type="file"
          disabled={disabled}
          readOnly={readOnly}
          aria-required={required}
          isInvalid={invalid}
          aria-invalid={invalid}
          aria-describedby={invalid ? errorId : fieldMessage ? fieldMessageId : undefined}
          className="fs__input"
          accept={accept}
          onChange={onChange}
          onBlur={onBlur}
        />

        {/* Validation feedback & Field message */}
        <FormUtils.ValidationTexts {...props} errorMessageId={errorId} fieldMessageId={fieldMessageId} />
      </FormUtils.Group>
    )
  },
  FormCheckInGroup: (props: FormBooleanFieldsetProps) => {
    const {
      controlId,
      extraGroupClasses,
      fieldMessage,
      items,
      label,
      disabled,
      readOnly,
      errorMessageId,
      invalid,
      onBlur,
    } = props
    const errorId = errorMessageId || `a11y-${controlId}-form-error`
    const fieldMessageId = `a11y-${controlId}-form-message`

    const checkboxesDivRef = React.useRef<HTMLDivElement>(null)
    const checkboxesFieldsetRef = React.useRef<HTMLFieldSetElement>(null)

    const FormCheck = (item: FormBooleanFieldsetItemProps) => (
      <Form.Check className={invalid ? fieldErrorClassName : ''}>
        <Form.Check.Input
          type="checkbox"
          name={`cb-${controlId}`}
          id={item.controlId}
          disabled={disabled}
          readOnly={readOnly}
          checked={item.checked}
          isInvalid={invalid ? true : undefined}
          aria-invalid={invalid}
          aria-describedby={invalid ? errorId : fieldMessage ? fieldMessageId : undefined}
          className="fs__boolean"
          onChange={(e) => item.onChange(e.currentTarget.checked)}
          onBlur={(e) => {
            if (checkboxesDivRef.current) {
              if (!checkboxesDivRef.current.matches(':focus-within') && onBlur) {
                onBlur(e)
              }
            }
            if (checkboxesFieldsetRef.current) {
              if (!checkboxesFieldsetRef.current.matches(':focus-within') && onBlur) {
                onBlur(e)
              }
            }
          }}
        />

        <Form.Check.Label className="text--styled" htmlFor={item.controlId}>
          {item.label}
          <FormUtils.OptionalLabel {...props} />
        </Form.Check.Label>
      </Form.Check>
    )
    return (
      // Render single checkbox
      <>
        {items.length && items.length == 1 ? (
          items.map((item) => (
            <FormUtils.Group
              {...props}
              key={`check-${item.controlId}`}
              groupAs={label ? 'fieldset' : 'div'}
              extraGroupClasses={`fs__group--checkbox ${extraGroupClasses || ''}`}
            >
              {label && <FormUtils.Legend {...props} />}
              <FormCheck {...item} />

              {/* Validation feedback & Field message */}
              <FormUtils.ValidationTexts {...props} errorMessageId={errorId} fieldMessageId={fieldMessageId} />
            </FormUtils.Group>
          ))
        ) : (
          // Render multiple checkboxes
          <FormUtils.Group
            {...props}
            groupAs="fieldset"
            extraGroupClasses={`fs__group--checkbox ${extraGroupClasses || ''}`}
          >
            {label && <FormUtils.Legend {...props} />}
            {items.length > 0 && items.map((item) => <FormCheck key={item.controlId} {...item} />)}

            {/* Validation feedback & Field message */}
            <FormUtils.ValidationTexts {...props} errorMessageId={errorId} fieldMessageId={fieldMessageId} />
          </FormUtils.Group>
        )}
      </>
    )
  },
  FormRadioInGroup: (props: FormBooleanFieldsetProps) => {
    const {
      controlId,
      extraGroupClasses,
      fieldMessage,
      items,
      required,
      disabled,
      readOnly,
      invalid,
      errorMessageId,
      onBlur,
    } = props
    const errorId = errorMessageId || `a11y-${controlId}-form-error`
    const fieldMessageId = `a11y-${controlId}-form-message`
    return (
      <FormUtils.Group
        {...props}
        groupAs="fieldset"
        extraGroupClasses={`fs__group--radio ${extraGroupClasses || ''}`}
        onBlur={onBlur}
      >
        <FormUtils.Legend {...props} />
        {items.length > 0 &&
          items.map((item) => {
            const { checked, controlId, label, onChange } = item
            return (
              <Form.Check key={controlId} className={invalid ? fieldErrorClassName : ''}>
                <Form.Check.Input
                  type="radio"
                  name={`rb-${controlId}`}
                  id={controlId}
                  aria-required={required}
                  disabled={disabled}
                  readOnly={readOnly}
                  checked={checked}
                  isInvalid={invalid ? true : undefined}
                  aria-invalid={invalid}
                  aria-describedby={invalid ? errorId : fieldMessage ? fieldMessageId : undefined}
                  className="fs__boolean"
                  onChange={(e) => onChange(e.currentTarget.checked)}
                />

                <Form.Check.Label className="text--styled" htmlFor={item.controlId}>
                  {label}
                  <FormUtils.OptionalLabel {...props} />
                </Form.Check.Label>
              </Form.Check>
            )
          })}

        {/* Validation feedback & Field message */}
        <FormUtils.ValidationTexts {...props} errorMessageId={errorId} fieldMessageId={fieldMessageId} />
      </FormUtils.Group>
    )
  },
  FormSwitchInGroup: (props: FormSwitchProps) => {
    const { checked, controlId, fieldMessage, label, onChange, onBlur, disabled, readOnly } = props
    const fieldMessageId = `a11y-${controlId}-form-message`
    return (
      <FormUtils.Group {...props} onBlur={onBlur}>
        <Form.Check
          type="switch"
          id={controlId}
          label={
            <>
              {label}
              <FormUtils.OptionalLabel {...props} />
            </>
          }
          checked={checked}
          disabled={disabled}
          readOnly={readOnly}
          onChange={(e) => onChange(e.currentTarget.checked)}
          aria-describedby={fieldMessage ? fieldMessageId : undefined}
        />

        {/* Field message */}
        {fieldMessage && <FormUtils.FieldText fieldMessageId={fieldMessageId} fieldMessage={fieldMessage} />}
      </FormUtils.Group>
    )
  },
}

// ==========================================================================
// NOTIFICATIONS
// ==========================================================================

export type FormNotificationProps = {
  message: string | JSX.Element
  status?: NotificationStatus
}

type NotificationStatus = 'error' | 'warning' | 'success' | 'info'

const alertClasses: { [key in NotificationStatus]: string } = {
  error: 'fs__alert--error',
  warning: 'fs__alert--warning',
  success: 'fs__alert--success',
  info: 'fs__alert--info',
}

export const FormNotification = (
  p: {
    showIcon?: boolean
    extraClasses?: string
  } & FormNotificationProps
) => {
  return (
    <Alert
      variant={(p.status == 'error' ? 'danger' : p.status) || 'danger'}
      className={cn(`fs__alert text--styled alert small ${alertClasses[p.status || 'error']}`, p.extraClasses)}
    >
      {typeof p.message == 'string' ? <p dangerouslySetInnerHTML={{ __html: p.message }} /> : p.message}
    </Alert>
  )
}
